import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDisabledValueChainNames } from '../shared/hooks/useDisabledValueChainNames';
import { useValueChainMetaInfo } from '../shared/hooks/useValueChainMetaInfo';
import { useAuth } from '../../components/auth/AuthProvider';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SankeyDiagram from '../shared/components/SankeyDiagram';
import FeatureNavTabs from '../shared/components/FeatureNavTabs';
import { getNodeTypeColor } from '../shared/utils/colors';
import hsCodeValueChainService from './hsCodeValueChainService';
import { api, toUserMessage } from '../../api/setup';
import CuratedNodeDetails from '../shared/components/CuratedNodeDetails';

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

// Types
interface GraphNode {
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
  source_db?: string;
}

interface SankeyData {
  nodes: Array<{
    name: any;
    value?: number;
    itemStyle?: { color: string };
    depth?: number;
    label?: any;
    nodeData: {
      type: any;
      properties: any;
      elementId: any;
      n_iteration: any;
    };
  }>;
  links: any[];
}

interface NodeDetails {
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
  type?: string;
}

const ProductAnalysisGraph: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [selectedElementId, setSelectedElementId] = useState<string>('');
  const [graphData, setGraphData] = useState<SankeyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableHSCodes, setAvailableHSCodes] = useState<GraphNode[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [maxIterations, setMaxIterations] = useState<number>(isMobile ? 2 : 4);
  const [minFlowThreshold, setMinFlowThreshold] = useState<number>(0.003);
  const [nodeNameToElementId, setNodeNameToElementId] = useState<Map<string, string>>(new Map());
  const [navigationHistory, setNavigationHistory] = useState<Array<{elementId: string, name: string}>>([]);
  const [selectedNodeDetails, setSelectedNodeDetails] = useState<NodeDetails | null>(null);
  const [loadingIncoming, setLoadingIncoming] = useState<boolean>(false);
  const [incomingNodes, setIncomingNodes] = useState<any[]>([]);
  // Section expansion UI removed; keep graph view simple
  // Persist the initially selected root for stable dropdown label and cross-view persistence
  const [selectedRootElementId, setSelectedRootElementId] = useState<string>('');
  const [selectedRootName, setSelectedRootName] = useState<string>('');
  const [selectedRootSourceDb, setSelectedRootSourceDb] = useState<string>('');
  const STORAGE_ID_KEY = 'vc.selectedRootId';
  const STORAGE_NAME_KEY = 'vc.selectedRootName';
  const STORAGE_SOURCE_DB_KEY = 'vc.selectedRootSourceDb';

  // Fetch value chain meta info (info and shortcomings) for the selected root using source_db
  const { info: valueChainInfo, shortcomings: valueChainShortcomings } = useValueChainMetaInfo(selectedRootSourceDb);
  
  // Names to show but keep disabled in the HS code dropdown
  const disabledValueChainNames = useDisabledValueChainNames();

  // Convert graph data to ECharts Sankey format
  const convertToSankeyFormat = useCallback((data) => {
    // Helper function to get node colors based on type using MUI theme
    const getNodeColor = (nodeType) => getNodeTypeColor(nodeType, theme);

    if (!data || !data.nodes || !data.relationships) {
      return { nodes: [], links: [] };
    }

    // Create a map of node IDs to names for easy lookup
    const nodeMap = new Map();
    const nodeNameCount = new Map();
    
    // Process nodes and handle duplicate names
    data.nodes.forEach(node => {
      const originalName = node.properties.name || `Node ${node.id}`;
      let finalName = originalName;
      
      // Handle duplicate names by appending a number
      if (nodeNameCount.has(originalName)) {
        const count = nodeNameCount.get(originalName) + 1;
        nodeNameCount.set(originalName, count);
        finalName = `${originalName} (${count})`;
      } else {
        nodeNameCount.set(originalName, 1);
      }
      
      nodeMap.set(node.id, {
        name: finalName,
        type: node.labels[0] || 'Unknown',
        n_iteration: node.properties.n_iteration || 0,
        properties: node.properties, // Store all original properties
        elementId: node.id // Store element ID for reference
      });
    });

    // Create nodes array for ECharts - let ECharts handle sizing automatically
    const sankeyNodes = Array.from(nodeMap.values()).map(node => ({
      name: node.name,
      itemStyle: {
        color: getNodeColor(node.type)
      },
      // Store additional data for tooltip and click handling
      nodeData: {
        type: node.type,
        properties: node.properties,
        elementId: node.elementId,
        n_iteration: node.n_iteration
      }
    }));

    // Store mapping from node names to element IDs for click handling
    const nameToElementIdMap = new Map();
    Array.from(nodeMap.entries()).forEach(([elementId, node]) => {
      nameToElementIdMap.set(node.name, elementId);
    });
    setNodeNameToElementId(nameToElementIdMap);

    // Process relationships to create links
    const sankeyLinks = [];
    const processedLinks = new Set(); // To avoid duplicate links
    
    data.relationships.forEach(rel => {
      // Include all relationship types for HS Code analysis (including GROUPED_PATH)
      const sourceNode = nodeMap.get(rel.start_node_id);
      const targetNode = nodeMap.get(rel.end_node_id);
      
      if (sourceNode && targetNode) {
        const linkKey = `${sourceNode.name}->${targetNode.name}`;
        
        // Avoid duplicate links
        if (!processedLinks.has(linkKey)) {
          processedLinks.add(linkKey);
          
          sankeyLinks.push({
            source: sourceNode.name,
            target: targetNode.name,
            value: (rel.properties?.path_weight ?? 0),
            lineStyle: {
              color: 'source',
              opacity: 0.45
            }
          });
        }
      }
    });

    return {
      nodes: sankeyNodes,
      links: sankeyLinks
    };
  }, [theme.palette]);

  // DEVELOPMENT MODE: Load all value chains from API instead of permission-filtered
  // TODO: Restore permission-based filtering when re-enabling permissions
  useEffect(() => {
    // Load all available roots from API with authentication
    (async () => {
      try {
        if (SHOULD_DEBUG) console.log('Fetching HS codes with authentication');
        const response = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
        if (SHOULD_DEBUG) console.log('Raw HS codes API response:', response);
        
        // API returns { success: true, data: [...], message: "..." }
        // Extract the actual data array
        const roots = Array.isArray(response?.data) ? response.data : 
                     Array.isArray(response) ? response : [];
        
        if (SHOULD_DEBUG) {
          console.log('Processed HS codes:', roots);
          console.log('Number of HS codes:', roots.length);
        }
        setAvailableHSCodes(roots);
        
        // If current selection is no longer available (e.g., after logout), clear it and select first public one
        if (selectedElementId) {
          const isStillAvailable = roots.some((r:any) => String(r.element_id) === String(selectedElementId));
          if (!isStillAvailable) {
            if (SHOULD_DEBUG) console.log('Selected root no longer available, selecting first available public root');
            setSelectedElementId('');
            setSelectedRootElementId('');
            setSelectedRootName('');
            setSelectedRootSourceDb('');
            setGraphData(null);
            // Automatically select first available public root
            if (roots.length > 0) {
              const firstPublic = roots[0];
              if (SHOULD_DEBUG) console.log('Auto-selecting first available root after logout:', firstPublic);
              const firstId = String(firstPublic.element_id);
              const firstName = String(firstPublic.name);
              const firstSourceDb = String(firstPublic.source_db || '');
              setSelectedElementId(firstId);
              setSelectedRootElementId(firstId);
              setSelectedRootName(firstName);
              setSelectedRootSourceDb(firstSourceDb);
              setNavigationHistory([{ elementId: firstPublic.element_id, name: firstPublic.name }]);
            }
          }
        }
        
        // Prefer persisted selection if available
        const savedId = String(localStorage.getItem(STORAGE_ID_KEY) || '');
        const savedName = String(localStorage.getItem(STORAGE_NAME_KEY) || '');
        const savedSourceDb = String(localStorage.getItem(STORAGE_SOURCE_DB_KEY) || '');
        if (!selectedElementId) {
          if (savedId) {
            const matchedRoot = roots.find((r:any)=>String(r.element_id)===savedId);
            if (matchedRoot) {
              setSelectedElementId(savedId);
              setSelectedRootElementId(savedId);
              setSelectedRootName(savedName || (matchedRoot?.name || ''));
              setSelectedRootSourceDb(savedSourceDb || (matchedRoot?.source_db || ''));
              setNavigationHistory([{ elementId: savedId, name: savedName || (matchedRoot?.name || `Element ${savedId}`) }]);
            } else if (roots.length > 0) {
              // If saved root not available, select first available
              const first = roots[0];
              if (SHOULD_DEBUG) console.log('Saved root not available, selecting first available:', first);
              const firstId = String(first.element_id);
              const firstName = String(first.name);
              const firstSourceDb = String(first.source_db || '');
              setSelectedElementId(firstId);
              setSelectedRootElementId(firstId);
              setSelectedRootName(firstName);
              setSelectedRootSourceDb(firstSourceDb);
              setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
            }
          } else if (roots.length > 0) {
            const first = roots[0];
            if (SHOULD_DEBUG) console.log('Setting first HS code:', first);
            const firstId = String(first.element_id);
            const firstName = String(first.name);
            const firstSourceDb = String(first.source_db || '');
            setSelectedElementId(firstId);
            setSelectedRootElementId(firstId);
            setSelectedRootName(firstName);
            setSelectedRootSourceDb(firstSourceDb);
            setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
          }
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('Error loading HS codes:', error);
        setAvailableHSCodes([]);
      }
    })();
  }, [isAuthenticated]); // Reload when authentication state changes

  /* ORIGINAL PERMISSION-BASED CODE - Uncomment to restore
  useEffect(() => {
    if (accessibleValueChains && accessibleValueChains.length > 0) {
      if (SHOULD_DEBUG) console.log('Setting available HSCodes from accessible value chains:', accessibleValueChains);
      setAvailableHSCodes(accessibleValueChains);
      if (!selectedElementId && accessibleValueChains.length > 0) {
        const first = accessibleValueChains[0];
        setSelectedElementId(String(first.element_id));
        setNavigationHistory([{ elementId: first.element_id, name: first.name }]);
      }
    } else {
      setAvailableHSCodes([]);
    }
  }, [accessibleValueChains, selectedElementId]);
  */

  // Fetch graph data when HS Code changes
  useEffect(() => {
    if (!selectedElementId) {
      setGraphData(null);
      return;
    }


    const fetchGraphData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await hsCodeValueChainService.getHSCodeValueChainData(selectedElementId, maxIterations, minFlowThreshold);
        const sankeyData = convertToSankeyFormat(data);
        setGraphData(sankeyData);
      } catch (err) {
        const { message, correlationId } = toUserMessage(err);
        setError(correlationId ? `${message} (Ref: ${correlationId})` : message);
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [selectedElementId, maxIterations, convertToSankeyFormat, availableHSCodes, isAuthenticated]);

  // Refetch only when threshold decreases (expand results)
  const prevThresholdRef = useRef(minFlowThreshold);
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      (async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await hsCodeValueChainService.getHSCodeValueChainData(selectedElementId, maxIterations, minFlowThreshold);
          const sankeyData = convertToSankeyFormat(data);
          setGraphData(sankeyData);
        } catch (err) {
          const { message, correlationId } = toUserMessage(err);
          setError(correlationId ? `${message} (Ref: ${correlationId})` : message);
        } finally {
          setLoading(false);
        }
      })();
    }
    prevThresholdRef.current = minFlowThreshold;
  }, [minFlowThreshold, selectedElementId, maxIterations, convertToSankeyFormat]);

  // Auto-fetch node details when graph data changes and we have a selected element
  useEffect(() => {
    if (graphData && selectedElementId) {
      // Find the node data for the selected element
      let foundNodeData = null;
      graphData.nodes.forEach(node => {
        const nodeData = node.nodeData;
        if (nodeData && nodeData.elementId === selectedElementId) {
          foundNodeData = nodeData;
        }
      });
      
      // Only update if we found the node and it's different from current
      if (foundNodeData && (!selectedNodeDetails || selectedNodeDetails.element_id !== foundNodeData.element_id)) {
        setSelectedNodeDetails(foundNodeData);
        // Also fetch incoming relationships for this node
        fetchIncomingRelationships(selectedElementId);
      }
    }
  }, [graphData, selectedElementId, selectedNodeDetails]);

  // Refetch incoming relationships when threshold changes (for current selection)
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      fetchIncomingRelationships(selectedElementId);
    }
  }, [minFlowThreshold, selectedElementId]);

  // Fetch incoming relationships for a specific node
  const fetchIncomingRelationships = async (hsCode) => {
    setLoadingIncoming(true);
    try {
      const directIncoming = await hsCodeValueChainService.getHSCodeIncomingRelationships(hsCode, minFlowThreshold);
      setIncomingNodes(directIncoming);
    } catch (err) {
      const { message } = toUserMessage(err);
      setError(message);
      setIncomingNodes([]);
    } finally {
      setLoadingIncoming(false);
    }
  };

  // Handle clicking on an incoming node
  const handleIncomingNodeClick = (elementId, nodeName) => {
    // Clear the detail card and incoming nodes first
    setSelectedNodeDetails(null);
    setIncomingNodes([]);
    
    // Then update the navigation state
    setSelectedElementId(elementId);
    setNavigationHistory(prev => [...prev, { elementId, name: nodeName }]);
  };

  const handleElementIdChange = (event) => {
    const newElementId = event.target.value;
    setSelectedElementId(newElementId);
    
    // Clear existing details when making a new selection
    setSelectedNodeDetails(null);
    setIncomingNodes([]);
    
    if (newElementId) {
      const selectedNode = availableHSCodes.find(node => node.element_id === newElementId);
      const nodeName = selectedNode ? selectedNode.name : `Element ${newElementId}`;
      const sourceDb = selectedNode?.source_db || '';
      // Persist the root selection for the dropdown label
      setSelectedRootElementId(String(newElementId));
      setSelectedRootName(String(nodeName));
      setSelectedRootSourceDb(String(sourceDb));
      try {
        localStorage.setItem(STORAGE_ID_KEY, String(newElementId));
        localStorage.setItem(STORAGE_NAME_KEY, String(nodeName));
        localStorage.setItem(STORAGE_SOURCE_DB_KEY, String(sourceDb));
      } catch {}
      setNavigationHistory([{ elementId: newElementId, name: nodeName }]);
    }
  };

  const handleNodeClick = (params) => {
    // Prefer elementId from node payload; fall back to name -> id map
    const payloadEl = params && params.data && params.data.nodeData ? params.data.nodeData.elementId : undefined;
    const byNameEl = nodeNameToElementId.get(params?.name);
    const elementId = String(payloadEl || byNameEl || '');

    if (!elementId) {
      if (SHOULD_DEBUG) console.warn('Could not find element ID for node:', params?.name);
      return;
    }

    // Store detailed node information for display
    setSelectedNodeDetails(params.data.nodeData);

    // Fetch incoming relationships for this node
    fetchIncomingRelationships(elementId);

    // Update the selected element to navigate to this node
    setSelectedElementId(elementId);

    // Add to navigation history
    setNavigationHistory(prev => [...prev, { elementId, name: params.name }]);
  };

  const handleNavigateBack = (index) => {
    if (index >= 0 && index < navigationHistory.length) {
      const targetHistory = navigationHistory[index];
      setSelectedElementId(targetHistory.elementId);
      // Truncate history to this point
      setNavigationHistory(prev => prev.slice(0, index + 1));
    }
  };

  const handleResetNavigation = () => {
    setNavigationHistory([]);
  };

  // DEVELOPMENT MODE: Permission checks commented out for public access
  // TODO: Uncomment these blocks to re-enable permission checks
  /*
  // Show loading state while permissions are loading
  if (permissionsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading value chain permissions...
        </Typography>
      </Box>
    );
  }

  // Show message if no accessible value chains
  if (!accessibleValueChains || accessibleValueChains.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Technology Types
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Value Chain Access
          </Typography>
          <Typography variant="body2">
            You don't have access to any value chains. Please contact your administrator to request access.
          </Typography>
        </Alert>
      </Box>
    );
  }
  */

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Technology Types
      </Typography>
      
      {/* Controls Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
        <Typography variant="body1" gutterBottom>Select a Value Chain to View its Technology Types</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click on a node to move up the value chain and see a more detailed breakdown of the technology types.
        </Typography>
        <Divider sx={{ my: 2, opacity: 0.5 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, overflowX: { xs: 'visible', md: 'auto' } }}>
          {/* HS Code Selection */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: { xs: '100%', md: 260 } }}>
            <Typography variant="body2" gutterBottom>
              Value Chain
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={String(selectedRootElementId || '')}
                onChange={(e) => {
                  const v = e.target.value; handleElementIdChange({ target: { value: String(v) } });
                }}
                displayEmpty
                sx={{ bgcolor: 'background.paper' }}
                renderValue={(val) => {
                  const id = String(val || '');
                  if (selectedRootElementId && id === String(selectedRootElementId) && selectedRootName) {
                    return selectedRootName;
                  }
                  const n = (availableHSCodes || []).find((x) => String(x.element_id) === id);
                  return n ? n.name : '';
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {(() => {
                  const existingNames = new Set((availableHSCodes || []).map(n => n.name));
                  const placeholders = Array.from(disabledValueChainNames).filter(n => !existingNames.has(n));
                  return (
                    <>
                      {(availableHSCodes || []).map((node) => {
                        const isDisabled = disabledValueChainNames.has(node.name);
                        return (
                          <MenuItem key={node.element_id} value={String(node.element_id)} disabled={isDisabled} onClick={!isDisabled ? () => handleElementIdChange({ target: { value: String(node.element_id) } }) : undefined} sx={isDisabled ? { color: theme.palette.text.disabled } : undefined}>
                            {node.name}
                          </MenuItem>
                        );
                      })}
                      {placeholders.map((name) => (
                        <MenuItem key={`disabled-${name}`} disabled sx={{ color: theme.palette.text.disabled }}>
                          {name}
                        </MenuItem>
                      ))}
                    </>
                  );
                })()}
              </Select>
            </FormControl>
          </Box>

          {/* Max Iterations Selector */}
          <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
              N Layers
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ maxWidth: 420 }}>
                    The number of layers defines how many steps upstream in the value chain you want to view.
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <input
                type="number"
                min="1"
                max="5"
                value={maxIterations}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1 && value <= 5) {
                    setMaxIterations(value);
                  }
                }}
                style={{ 
                  width: '60px',
                  padding: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </Box>
          </Box>

          {/* Min Flow Threshold Selector */}
          <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
                Min Value
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 460 }}>
                    {'This filter removes very small flows from the chart.\n'}
                    {'Flows are combined for each source-to-target connection, then the minimum value is applied.\n'}
                    {'In other words, it keeps meaningful links and hides minor ones.'}
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ position: 'relative', width: '84px' }}>
              <input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={(minFlowThreshold * 100).toFixed(1)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (value >= 0.1 && value <= 10) {
                    setMinFlowThreshold(value / 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 24px 8px 8px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  lineHeight: 1,
                }}
              >
                %
              </Typography>
            </Box>
          </Box>

          {/* Navigation History */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: { xs: '100%', md: 260 } }}>
            <Typography variant="body2" gutterBottom>
              Navigation History
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {navigationHistory.map((item, index) => (
                <Chip
                  key={index}
                  label={item.name}
                  onClick={() => handleNavigateBack(index)}
                  color={index === navigationHistory.length - 1 ? "primary" : "default"}
                  variant={index === navigationHistory.length - 1 ? "filled" : "outlined"}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
              {navigationHistory.length > 0 && (
                <Tooltip title="Reset Navigation">
                  <IconButton
                    size="small"
                    onClick={handleResetNavigation}
                    sx={{ color: theme.palette.error.main }}
                  >
                    ✕
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 2, opacity: 0.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The number of layers defines how many steps upstream in the value chain you want to view.
          The minimum value threshold specifies the lowest percentage a value flow must have in order to be represented.
        </Typography>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Sankey Diagram */}
      <SankeyDiagram
        data={graphData}
        loading={loading}
        error={error}
        onNodeClick={handleNodeClick}
        title={`${(navigationHistory[navigationHistory.length - 1]?.name) || (availableHSCodes.find(n => n.element_id === selectedElementId)?.name) || 'Selected'} Value Chain in Technology Types`}
        height={600}
        minFlowThreshold={minFlowThreshold}
        rootElementId={selectedElementId}
        requireRootConnectivity
        enablePersistentFocus={false}
        valueChainInfo={valueChainInfo}
        valueChainShortcomings={valueChainShortcomings}
        legend={(
          <Box sx={{ mt: 1 }}>
            {(() => {
              const Swatch = ({ color }) => (
                <Box sx={{ width: 28, height: 6, borderRadius: 3, bgcolor: color, opacity: 0.45, border: '1px solid', borderColor: (t) => t.palette.divider, mr: 1 }} />
              );
              const computeFlowLegendColor = () => {
                try {
                  const links = (graphData && Array.isArray(graphData.links)) ? graphData.links : [];
                  if (links.length === 0) return '#42a5f5';
                  const l = links[0] || {};
                  let c = l?.lineStyle?.color;
                  if (!c || c === 'source') {
                    const nodes = (graphData && Array.isArray(graphData.nodes)) ? graphData.nodes : [];
                    const src = nodes.find(n => n && n.name === l.source);
                    c = src?.itemStyle?.color || '#42a5f5';
                  }
                  return c;
                } catch { return '#42a5f5'; }
              };
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr', md: '3fr 1fr' }, gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>Legend (flows)</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Each component or material can appear in the form of several technology types, with a prevalence weight that tells us how common that type is.

                      Each technology type is made up of different components/materials, each with an estimated cost-share weight that approximates the relative cost share of that component in the mix of other components, materials, labor, etc.

                      When we connect one technology type to another through a component (Tech A → Component → Tech B), we calculate a path weight by multiplying the prevalence (from Tech A to the component) with the cost share (from the component to Tech B).

                      The product of this multiplication is the flow weight for each technology type path represented in the diagram.
                    </Typography>
                  </Box>
                  <Paper elevation={0} variant="outlined" sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.5)', borderColor: (t) => t.palette.divider }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Flow Weights</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                      <Swatch color={computeFlowLegendColor()} />
                      <Typography variant="caption" color="text.secondary">Path Flow Weight (cost share × prevalence)</Typography>
                    </Box>
                  </Paper>
                </Box>
              );
            })()}
          </Box>
        )}
      />

      {/* Local Navigation Tabs directly under the Sankey */}
      <FeatureNavTabs sx={{ mb: 2 }} />

      {/* Node Details Card */}
      {selectedNodeDetails && (
        <>
        <CuratedNodeDetails node={{ type: selectedNodeDetails.type as any, properties: selectedNodeDetails.properties || {} }} />
        <Paper elevation={1} sx={{ p: 3, mt: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>
          {/* Incoming Nodes Section */}
          <Typography variant="h6" gutterBottom>
            Direct Incoming Relationships
          </Typography>
          {loadingIncoming ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : incomingNodes.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(auto-fill, minmax(260px, 1fr))' }, gap: 2, minWidth: 0 }}>
              {incomingNodes.map((item, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: theme.palette.action.hover }, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}
                  onClick={() => handleIncomingNodeClick(item.sourceNode.id, item.sourceNode.properties.name)}
                >
                  <Typography variant="subtitle2" gutterBottom sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {item.sourceNode.properties.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type: {item.sourceNode.labels[0]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Relationship: {item.relationshipType}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weight: {item.weight}
                  </Typography>
                  {/* Element ID intentionally hidden */}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No direct incoming relationships found.
            </Typography>
          )}
        </Paper>
        </>
      )}
    </Box>
  );
};

export default ProductAnalysisGraph;
